import { useEffect, useRef, useCallback } from 'react';

interface LipMark {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  opacity: number;
  createdAt: number;
  lifetime: number;
}

interface TigerRipple {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
  createdAt: number;
  stripes: { angle: number; width: number; offset: number }[];
}

export default function InteractiveCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lipMarksRef = useRef<LipMark[]>([]);
  const tigerRipplesRef = useRef<TigerRipple[]>([]);
  const animFrameRef = useRef<number>(0);
  const lastLipTimeRef = useRef<number>(0);

  // Draw a stylised lip shape on canvas
  const drawLip = useCallback((
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    scale: number,
    rotation: number,
    alpha: number
  ) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.globalAlpha = alpha;

    const w = 38 * scale;
    const h = 18 * scale;

    // Upper lip
    ctx.beginPath();
    ctx.moveTo(-w * 0.5, 0);
    ctx.bezierCurveTo(-w * 0.35, -h * 0.9, -w * 0.15, -h * 1.1, 0, -h * 0.5);
    ctx.bezierCurveTo(w * 0.15, -h * 1.1, w * 0.35, -h * 0.9, w * 0.5, 0);
    // Lower lip
    ctx.bezierCurveTo(w * 0.3, h * 1.1, -w * 0.3, h * 1.1, -w * 0.5, 0);
    ctx.closePath();

    // Wine red fill with slight variation
    const r = 77 + Math.floor(Math.random() * 20 - 10);
    const g = 24 + Math.floor(Math.random() * 10 - 5);
    const b = 21 + Math.floor(Math.random() * 10 - 5);
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fill();

    // Subtle highlight
    ctx.beginPath();
    ctx.ellipse(-w * 0.1, -h * 0.1, w * 0.18, h * 0.12, -0.2, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(234,221,204,0.12)`;
    ctx.fill();

    ctx.restore();
  }, []);

  // Draw tiger ripple
  const drawTigerRipple = useCallback((
    ctx: CanvasRenderingContext2D,
    ripple: TigerRipple
  ) => {
    const { x, y, radius, maxRadius, opacity, stripes } = ripple;
    if (opacity <= 0) return;

    ctx.save();
    ctx.globalAlpha = opacity;

    // Outer glow ring
    const gradient = ctx.createRadialGradient(x, y, radius * 0.6, x, y, radius);
    gradient.addColorStop(0, 'rgba(77,24,21,0)');
    gradient.addColorStop(0.7, `rgba(77,24,21,${opacity * 0.35})`);
    gradient.addColorStop(1, 'rgba(77,24,21,0)');
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Tiger stripes radiating outward
    stripes.forEach((stripe) => {
      const progress = radius / maxRadius;
      const stripeLen = radius * 0.55 * progress;
      const stripeStart = radius * 0.18;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(stripe.angle + stripe.offset);

      ctx.beginPath();
      ctx.moveTo(stripeStart, -stripe.width * 0.5);
      ctx.lineTo(stripeStart + stripeLen, -stripe.width * 0.8);
      ctx.lineTo(stripeStart + stripeLen * 1.15, 0);
      ctx.lineTo(stripeStart + stripeLen, stripe.width * 0.8);
      ctx.lineTo(stripeStart, stripe.width * 0.5);
      ctx.closePath();

      ctx.fillStyle = `rgba(77,24,21,${opacity * 0.55})`;
      ctx.fill();
      ctx.restore();
    });

    // Inner circle pulse
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.12, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(234,221,204,${opacity * 0.4})`;
    ctx.fill();

    ctx.restore();
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const now = Date.now();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw tiger ripples
    tigerRipplesRef.current = tigerRipplesRef.current.filter((r) => {
      const age = now - r.createdAt;
      const duration = 1800;
      if (age > duration) return false;
      const progress = age / duration;
      r.radius = r.maxRadius * Math.pow(progress, 0.55);
      r.opacity = 1 - Math.pow(progress, 1.4);
      drawTigerRipple(ctx, r);
      return true;
    });

    // Draw lip marks
    lipMarksRef.current = lipMarksRef.current.filter((lm) => {
      const age = now - lm.createdAt;
      if (age > lm.lifetime) return false;
      const fadeStart = lm.lifetime * 0.55;
      const alpha = age < fadeStart
        ? lm.opacity * Math.min(1, age / 200)
        : lm.opacity * (1 - (age - fadeStart) / (lm.lifetime - fadeStart));
      drawLip(ctx, lm.x, lm.y, lm.scale, lm.rotation, Math.max(0, alpha));
      return true;
    });

    animFrameRef.current = requestAnimationFrame(animate);
  }, [drawLip, drawTigerRipple]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    animFrameRef.current = requestAnimationFrame(animate);

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastLipTimeRef.current < 120) return;
      lastLipTimeRef.current = now;

      lipMarksRef.current.push({
        x: e.clientX + (Math.random() - 0.5) * 18,
        y: e.clientY + (Math.random() - 0.5) * 18,
        rotation: (Math.random() - 0.5) * 0.7,
        scale: 0.55 + Math.random() * 0.55,
        opacity: 0.25 + Math.random() * 0.35,
        createdAt: now,
        lifetime: 2800 + Math.random() * 1800,
      });

      // Keep max 60 lip marks
      if (lipMarksRef.current.length > 60) {
        lipMarksRef.current.shift();
      }
    };

    const handleClick = (e: MouseEvent) => {
      const stripeCount = 10 + Math.floor(Math.random() * 6);
      const stripes = Array.from({ length: stripeCount }, (_, i) => ({
        angle: (i / stripeCount) * Math.PI * 2,
        width: 6 + Math.random() * 10,
        offset: (Math.random() - 0.5) * 0.4,
      }));

      tigerRipplesRef.current.push({
        x: e.clientX,
        y: e.clientY,
        radius: 0,
        maxRadius: 220 + Math.random() * 140,
        opacity: 1,
        createdAt: Date.now(),
        stripes,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [animate]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-10 pointer-events-none"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}
